import { useCallback, useState } from 'react'

import { useUnit } from 'effector-react'
import { useNavigate } from 'react-router'

import IconLoading from '@/assets/icons/icon-loading.svg'
import NotFoundImage from '@/assets/images/not-found.svg'
import { $currentCreator } from '@/models/creators.ts'
import { AppRoutes } from '@/utils/constants'

import { AddToList } from '../../../ui/AddToList.tsx'
import type { SoundType } from '../../types'
import { SoundCard } from './SoundCard.tsx'

interface Props {
  sounds: SoundType[]
  lastSoundElement: (element: HTMLDivElement) => void
  isFetching?: boolean
}

export const SoundsGrid = ({ sounds, lastSoundElement, isFetching }: Props) => {
  const activeCreator = useUnit($currentCreator)
  const navigate = useNavigate()
  const [selectedSoundsMap, setSelectedSoundsMap] = useState<
    Map<SoundType['id'], boolean>
  >(new Map())

  const handleToggleVideo = useCallback((soundId: SoundType['id']) => {
    setSelectedSoundsMap((prev) => {
      const newMap = new Map(prev)

      newMap.set(soundId, !prev.get(soundId))

      return newMap
    })
  }, [])

  const selectedSounds = [...selectedSoundsMap.entries()]
    .filter(([, value]) => value)
    .map(([key]) => key)

  const resetSelection = () => {
    setSelectedSoundsMap(new Map())
  }

  const handleClickViewVideos = useCallback(
    (soundId: number) => {
      if (activeCreator) {
        navigate(
          AppRoutes.TiktokResearch.Sounds.TrendingVideos.asUrl(
            activeCreator.id,
            String(soundId),
          ),
        )
      }
    },
    [activeCreator, navigate],
  )

  if (sounds.length === 0) {
    return (
      <div className="flex flex-col items-center rounded-lg p-4">
        <NotFoundImage
          width={80}
          height={80}
        />
        <p className="text-secondary-300 text-center text-sm font-medium">
          No sounds found with selected filters
        </p>
      </div>
    )
  }

  return (
    <>
      <div className="grid grid-cols-1 gap-6 md:grid-cols-3 lg:grid-cols-4">
        {sounds.map((sound, i, { length }) => (
          <SoundCard
            ref={i === length - 1 ? lastSoundElement : undefined}
            item={sound}
            key={sound.id}
            toggleSelect={handleToggleVideo}
            isSelected={selectedSoundsMap.get(sound.id) ?? false}
            onClickViewVideos={() => handleClickViewVideos(sound.id)}
          />
        ))}
      </div>
      {isFetching && (
        <div className="my-3 flex items-center justify-center gap-2">
          <IconLoading className="text-secondary-300 h-4 w-4 animate-spin" />{' '}
          Loading...
        </div>
      )}
      <AddToList
        mediaType="sounds"
        selectedItems={selectedSounds}
        onAdd={resetSelection}
        title="Move Sounds to List"
        description="Choose the list you want to add the selected sounds to or create a new list"
      />
    </>
  )
}
